// --------------------------------------------------------------
// Created On: 2024-04-01
// Author: Dimitra Weinstein
//
// Last Modified: 2026-01-19
// Modified By: Zachary Thomas
//
// Copyright 2024 - 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { Fragment, useEffect, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { useReactToPrint } from "react-to-print";
import styles from "./PrintBom.module.scss";
import LeadTime from "../../../../components/LeadTime/LeadTime";
import formatPrice from "../../../../utilities/formatPrice";
import Spinner from "../../../../components/Spinner/Spinner";

// Renders flat list of parts from a bom.
export default function PrintBom(props: Props): Component {
  const [loadLeadTime, setLoadLeadTime] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [leadTimeLoadedCount, setLeadTimeLoadedCount] = useState<number>(0);
  const leadTimeAllLoaded = leadTimeLoadedCount === props.parts.length;
  const contentToPrint = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({
    contentRef: contentToPrint,
    documentTitle: props.bomNumber,
  });

  useEffect(() => {
    // Only pull up the print modal once the last leadTime has finished loading for the first time
    if (loadLeadTime && leadTimeAllLoaded) {
      handlePrint();
      setLoading(false);
    }
  }, [leadTimeAllLoaded, loadLeadTime]);

  return (
    <Fragment>
      <div className={styles.printContent} ref={contentToPrint}>
        <h5 className={styles.pdfTitle}>{props.bomNumber}</h5>
        <table className={`${styles.table} table mb-0 pb-0`}>
          <thead>
            <tr>
              <th>Part Number</th>
              <th>Description</th>
              <th>Quantity</th>
              <th>Price</th>
              {props.companyDiscount && <th>Discounted Price</th>}
              <th>Weight</th>
              <th>Ship Time</th>
            </tr>
          </thead>
          <tbody>
            {props.parts.map((part, index) => (
              <tr key={index}>
                <td>{part.partNumber}</td>
                <td>{part.description}</td>
                <td>
                  {part.buildQuantity}&nbsp;{part.units}
                </td>
                <td>{formatPrice(part.price)}</td>
                {props.companyDiscount && (
                  <td>{formatPrice(part.price - part.price * (props.companyDiscount / 100))}</td>
                )}
                <td>{part.weight} lbs</td>
                <td>
                  {loadLeadTime && (
                    <LeadTime
                      quantity={part.orderQuantity}
                      partNumber={part.partNumber}
                      className=""
                      paintColorId={part.isPaintable ? part.paintColorId : null}
                      isImpeller={part.isImpeller}
                      impellerAngle=""
                      impellerTrim=""
                      onLeadTimeCalculated={() => {
                        setLeadTimeLoadedCount((prev: number) => prev + 1);
                      }}
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <span className="col-12 justify-content-center mx-2 mt-3 d-block text-center">
          &#42; Ship time is calculated from the current date in Pacific Standard/Daylight Time.
        </span>
      </div>
      <Spinner loading={loading} />
      <Button
        type="button"
        data-test="print-bom-btn"
        className="btn btn-primary btn-md w-100 py-3 my-0 mb-2 mb-xl-0"
        disabled={loading}
        onClick={() => {
          // Start the loading process for leadTimes on the first click otherwise just pull up the print modal
          if (leadTimeAllLoaded) {
            handlePrint();
          } else {
            setLoadLeadTime(true);
            setLoading(true);
          }
        }}
      >
        <span className="d-none d-md-inline">Print BOM</span>
        <i className="d-inline d-md-none fa fa-2x fa-print" />
      </Button>
    </Fragment>
  );
}

interface Props {
  bomNumber: string;
  parts: Part[];
  companyDiscount: number | null;
}

interface Part {
  partNumber: string;
  description: string;
  buildQuantity: string;
  orderQuantity: string;
  paintColorId: number | null;
  isPaintable: boolean;
  isSellable: boolean;
  isImpeller: boolean;
  units: string;
  price: number;
  weight: number;
}
